/**
 * useNovaIntelligence — NOVA-OFF-1's setting.
 *
 * Off stops everything that sends anything to a model: the chat, her
 * voice, and the suggestions she writes. Nova herself stays — she is
 * the character on the dashboard and in the Passport, and switching
 * off the intelligence shouldn't delete the mascot.
 *
 * The setting lives in localStorage under `orbit.novaIntelligence`,
 * the key iOS keeps in UserDefaults, so the two apps agree. Unset is
 * ON: an assistant that arrives switched off is one nobody discovers.
 *
 * Built the same way as `useSeasonalThemes` — one module-level store,
 * `useSyncExternalStore`, and a `storage` listener so another tab
 * flipping the switch lands here too.
 */
import { useCallback, useSyncExternalStore } from 'react';

const STORAGE_KEY = 'orbit.novaIntelligence';

// Private mode can refuse storage; the switch still works for the tab.
let memoryEnabled = true;

const listeners = new Set<() => void>();

function emit() {
  listeners.forEach((listener) => listener());
}

function subscribe(listener: () => void) {
  listeners.add(listener);
  const onStorage = (event: StorageEvent) => {
    if (event.key === STORAGE_KEY || event.key === null) listener();
  };
  window.addEventListener('storage', onStorage);
  return () => {
    listeners.delete(listener);
    window.removeEventListener('storage', onStorage);
  };
}

function read(): boolean {
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    return raw === null ? memoryEnabled : raw !== '0';
  } catch {
    return memoryEnabled;
  }
}

/**
 * Readable outside React — the guard in the chat transport, where
 * there is no hook to call.
 */
export function novaIntelligenceOn(): boolean {
  return read();
}

export function setNovaIntelligence(on: boolean) {
  memoryEnabled = on;
  try {
    window.localStorage.setItem(STORAGE_KEY, on ? '1' : '0');
  } catch {
    // Storage refused; the tab still honours it.
  }
  emit();
}

/** `[on, setOn]`, the shape `useSeasonalThemes` already uses. */
export function useNovaIntelligence(): [boolean, (next: boolean) => void] {
  const on = useSyncExternalStore(subscribe, read, () => true);
  const set = useCallback((next: boolean) => setNovaIntelligence(next), []);
  return [on, set];
}
